import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Fades in (and lifts up slightly) the content of the About page as it scrolls into view.
 *
 * Which elements are hidden until revealed is decided in custom.css (gated by `html.about-fade`,
 * which is set from the head before first paint by snippets/about-scroll-fade.liquid). This module
 * only observes those elements and marks them `.is-inview` once they enter the viewport.
 */

const TARGET_SELECTOR = [
  'img',
  'p',
  'a',
  'video',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  // Only "leaf" divs: a wrapper div fading in around its own fading children would double up.
  'div:not(:has(img, p, h1, h2, h3, h4, h5, h6, div))',
].join(',');

/** Matches the transition duration in custom.css; after it the reveal styles are dropped. */
const FALLBACK_DONE_MS = 1400;

/** @type {IntersectionObserver | null} */
let observer = null;

/** @type {Element[]} */
let pending = [];

/**
 * @param {Element} element
 */
function reveal(element) {
  element.classList.add('is-inview');
  observer?.unobserve(element);
  pending = pending.filter((item) => item !== element);

  // Drop the reveal transition afterwards so it doesn't override the element's own transitions (hover etc.).
  setTimeout(() => element.classList.add('is-done'), FALLBACK_DONE_MS);
}

function observe() {
  observer?.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target);
      }
    },
    // `.page-wrapper` is the scroll container on desktop, the viewport on mobile.
    { root: getIntersectionRoot(), rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  for (const element of pending) observer.observe(element);
}

function init() {
  const main = document.querySelector('#MainContent[data-template="page.about"]');
  if (!main) return;

  pending = Array.from(main.querySelectorAll(TARGET_SELECTOR));
  observe();
  scrollContainerMediaQuery.addEventListener('change', observe);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
